import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ShieldCheck, Search, FileText, ClipboardList } from 'lucide-react';
import { Button } from '../components/ui/button';

const CustomersPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  return (
    <main className="pt-16 md:pt-20" style={{ fontFamily: "'Open Sans', sans-serif" }} data-testid="customers-page">
      <Helmet>
        <title>Hire local on real infrastructure — Servitium for Customers</title>
        <meta name="description" content="The infrastructure that runs the work when you hire local — verified identity, written quotes, in-app payment, and a real review record. Free for customers." />
        <meta property="og:title" content="Hire local on real infrastructure — Servitium for Customers" />
        <meta property="og:description" content="The infrastructure that runs the work when you hire local — verified identity, written quotes, in-app payment, and a real review record. Free for customers." />
        <meta property="og:url" content="https://servitium.ca/customers" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Hire local on real infrastructure — Servitium for Customers" />
        <meta name="twitter:description" content="The infrastructure that runs the work when you hire local — verified identity, written quotes, in-app payment, and a real review record. Free for customers." />
        <meta property="og:image" content="https://servitium.ca/og-customers.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Servitium for Customers — Hire local on real infrastructure" />
        <meta name="twitter:image" content="https://servitium.ca/og-customers.png" />
      </Helmet>

      {/* ══ SECTION 1: HERO ══ */}
      <section className="py-20 md:py-28 bg-[#FAFAF9]" data-testid="customers-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">

            {/* Left column — text */}
            <div className="text-center lg:text-left" data-testid="customers-hero-text">
              <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-[#880808] mb-4">
                EVERY STEP, INSIDE THE APP
              </p>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#36454F] tracking-tight leading-[1.08] mb-6"
                data-testid="customers-hero-headline"
              >
                WORKING WITH A LOCAL BUSINESS SHOULDN'T BE A GUESSING GAME.
              </h1>
              <p className="text-base md:text-lg text-[#57534E] leading-relaxed mb-8 max-w-[600px] mx-auto lg:mx-0">
                Servitium is the infrastructure that runs the work when you hire a solo operator — verified identity, written quotes, scheduled jobs, in-app payment, and a real review record. Every part on the record. Nothing left to a text thread and a handshake.
              </p>

              {/* CTA button */}
              <div className="flex items-center justify-center lg:justify-start mb-4">
                <Link to="/customers/get" data-testid="customers-cta-download">
                  <Button className="bg-[#880808] hover:bg-[#6b0606] text-white rounded-full px-10 py-5 text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105">
                    Download
                  </Button>
                </Link>
              </div>

              {/* Microcopy */}
              <p className="text-sm italic text-[#6b7280] mb-6" data-testid="customers-hero-microcopy">
                Free for customers. Always.
              </p>

            </div>

            {/* Right column — iPhone 16 Pro Max mockup */}
            <div className="flex justify-center lg:justify-end lg:items-center" data-testid="customers-hero-mockup">
              <div
                className="relative w-[240px] md:w-[260px] h-[510px] md:h-[555px] bg-[#1C1917] rounded-[45px] p-[8px] shadow-2xl"
                aria-label="iPhone 16 Pro Max showing Servitium job timeline"
              >
                {/* Side buttons (subtle) */}
                <div className="absolute -left-[3px] top-[88px] w-[3px] h-[22px] bg-[#1C1917] rounded-l-sm" />
                <div className="absolute -left-[3px] top-[128px] w-[3px] h-[40px] bg-[#1C1917] rounded-l-sm" />
                <div className="absolute -left-[3px] top-[178px] w-[3px] h-[40px] bg-[#1C1917] rounded-l-sm" />
                <div className="absolute -right-[3px] top-[138px] w-[3px] h-[64px] bg-[#1C1917] rounded-r-sm" />

                {/* Screen */}
                <div className="relative w-full h-full bg-[#FAFAF9] rounded-[37px] overflow-hidden">
                  {/* Autoplay video — WebM first (Chromium), MP4 fallback (Safari/iOS) */}
                  <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="customers-hero-video"
                  >
                    <source src="/customers-hero.webm" type="video/webm" />
                    <source src="/customers-hero.mp4" type="video/mp4" />
                  </video>

                  {/* Home indicator */}
                  <div className="absolute bottom-[8px] left-1/2 -translate-x-1/2 w-[90px] h-[3.5px] bg-white/40 rounded-full z-20" />
                </div>
              </div>
            </div>
          </div>

          {/* Supporters strip — centered across the full hero width */}
          <div className="mt-16 md:mt-20 flex flex-col items-center" data-testid="customers-supporters-strip">
            <p
              className="text-[13px] text-[#6b7280] mb-4 text-center"
              data-testid="customers-supporters-microcopy"
            >
              Free across the Lower Mainland · Supported by Futurpreneur Canada & BDC
            </p>
            <div
              className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4"
              data-testid="customers-supporter-badges"
            >
              {/* Futurpreneur Badge */}
              <a
                href="https://futurpreneur.ca/en/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Futurpreneur Canada (opens in new tab)"
                className="block w-full sm:w-auto max-w-[340px] sm:max-w-none"
                data-testid="customers-badge-futurpreneur"
              >
                <div className="bg-white rounded-xl p-4 md:p-5 shadow-[0_2px_12px_rgba(0,0,0,0.06)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.1)] hover:-translate-y-1 transition-all duration-200 ease-out cursor-pointer w-full sm:w-[240px] md:w-[260px] h-[88px] md:h-[100px] flex items-center justify-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/efnyjxv1_FP%20card.png"
                    alt="Futurpreneur Canada"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              </a>

              {/* BDC Badge */}
              <a
                href="https://www.bdc.ca/en?utm_source=google&utm_medium=cpc&utm_campaign=brand-core_lead_sem_google_en_all_can&utm_term=bdc&utm_id=22860846897&utm_source_platform=Google+Ads&utm_content=1_Brand_Core&gclsrc=aw.ds&gad_source=1&gad_campaignid=22860846897&gbraid=0AAAAAD1PdqM9bWGnDlFgtc3IsKj56_9TJ&gclid=CjwKCAiAssfLBhBDEiwAcLpwftI4UuYSSTKTKurZynf8YD4V3imCT9mPSMpLlXnryxMAnMc15E9txxoCKMYQAvD_BwE"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="BDC (opens in new tab)"
                className="block w-full sm:w-auto max-w-[340px] sm:max-w-none"
                data-testid="customers-badge-bdc"
              >
                <div className="bg-white rounded-xl p-4 md:p-5 shadow-[0_2px_12px_rgba(0,0,0,0.06)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.1)] hover:-translate-y-1 transition-all duration-200 ease-out cursor-pointer w-full sm:w-[240px] md:w-[260px] h-[88px] md:h-[100px] flex items-center justify-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_359af3e7-c360-43fc-95d6-c1a44e2ac94a/artifacts/5vqtbwia_BDC%20card.png"
                    alt="BDC - Business Development Bank of Canada"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ══ SECTION 2: THE IMPROVISATION PROBLEM ══ */}
      <section className="py-12 md:py-20 bg-[#F8F6F0]" data-testid="customers-improvisation">
        <div className="max-w-[720px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#1C1917] tracking-tight leading-tight mb-12"
            data-testid="customers-improvisation-headline"
          >
            RIGHT NOW, EVERY TIME YOU HIRE SOMEONE LOCAL, YOU'RE IMPROVISING.
          </h2>

          <p className="text-lg md:text-xl text-[#1C1917] leading-relaxed mb-6">
            The text thread with the cleaner who said they'd come Tuesday.
          </p>
          <p className="text-lg md:text-xl text-[#1C1917] leading-relaxed mb-6">
            The landscaper who quoted verbally and the price changed.
          </p>
          <p className="text-lg md:text-xl text-[#1C1917] leading-relaxed mb-6">
            The contractor you paid in cash and never heard from again.
          </p>
          <p className="text-lg md:text-xl text-[#1C1917] leading-relaxed mb-6">
            The Google review that says nothing about the actual job that was done.
          </p>
          <p className="text-lg md:text-xl text-[#1C1917] leading-relaxed">
            The "verified" badge that nobody actually verified.
          </p>

          <p className="text-lg md:text-xl italic text-[#1C1917] leading-relaxed mt-12">
            It's not your fault. Until now, there was no infrastructure for any of it.
          </p>
        </div>
      </section>

      {/* ══ SECTION 3: WHAT CHANGES ══ */}
      <section className="py-20 md:py-28 bg-[#FAFAF9]" data-testid="customers-what-changes">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#36454F] tracking-tight leading-tight text-center mb-12"
            data-testid="customers-what-changes-headline"
          >
            WHEN THE BUSINESS RUNS ON SERVITIUM, THE WHOLE JOB CHANGES FOR YOU.
          </h2>

          {/* TOP HERO card — Real-time updates (NEW) */}
          <div
            className="bg-[#F8F6F0] rounded-2xl overflow-hidden mb-8 shadow-[0_4px_24px_rgba(0,0,0,0.08)]"
            data-testid="customers-change-top-hero-card"
          >
            <div className="h-[5px] bg-[#880808] w-full" />
            <div className="p-8 md:p-12 lg:p-14 text-center">
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#36454F] tracking-tight leading-tight mb-4">
                YOU SEE EVERY STEP IN REAL TIME.
              </h3>
              <p className="text-base md:text-lg text-[#57534E] leading-relaxed max-w-3xl mx-auto">
                Quote received. Confirmed. Job scheduled. Work in progress. Payment complete. Every update reaches you in the app the moment it happens — no chasing, no "any updates?" texts.
              </p>
            </div>
          </div>

          {/* 2×2 supporting cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {[
              {
                title: 'THEY CONTACT YOU ON YOUR TERMS.',
                body: "Businesses can never message you first on Servitium. You decide when to reach out — by quote request, call, email, or in-app message — and they respond. No spam. No follow-up calls you didn't ask for.",
              },
              {
                title: 'ONE PLACE. NOT FIVE APPS.',
                body: 'Quote, schedule, message, pay, review — all in one app. No more switching between texts, e-transfer, calendar, and email to keep track of one job.',
              },
              {
                title: 'THEIR RECORD FOLLOWS THEM.',
                body: "A business's reputation on Servitium is built across every job they complete and stays with them. Years from now, the work they did for you is still part of their record.",
              },
              {
                title: 'YOU CHOOSE ON SIGNALS, NOT ON WHOEVER TEXTED BACK FIRST.',
                body: "Every business shows their verified identity, the credentials they've added, and their job history — so you're choosing on real signals, not on whoever happened to be available first.",
              },
            ].map((card, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.1)] hover:-translate-y-1 transition-all duration-200 ease-out flex flex-col"
                data-testid={`customers-change-card-${i}`}
              >
                <div className="h-[4px] bg-[#D4AF37] w-full" />
                <div className="p-6 md:p-8 flex flex-col flex-grow text-center">
                  <h3 className="text-lg md:text-xl font-extrabold text-[#1C1917] tracking-tight leading-tight mb-3">
                    {card.title}
                  </h3>
                  <p className="text-[#57534E] text-sm md:text-base leading-relaxed flex-grow">
                    {card.body}
                  </p>
                </div>
                <div className="h-[3px] bg-[#E7E5E4] w-full" />
              </div>
            ))}
          </div>

          {/* BOTTOM EMPHASIS card — Real record (MOVED from top) */}
          <div
            className="bg-[#F8F6F0] rounded-2xl overflow-hidden mt-8 shadow-[0_4px_24px_rgba(0,0,0,0.08)]"
            data-testid="customers-change-bottom-emphasis-card"
          >
            <div className="h-[5px] bg-[#880808] w-full" />
            <div className="p-8 md:p-12 lg:p-14 text-center">
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#36454F] tracking-tight leading-tight mb-4">
                YOU SEE A REAL RECORD, NOT A CURATED BROCHURE.
              </h3>
              <p className="text-base md:text-lg text-[#57534E] leading-relaxed max-w-3xl mx-auto">
                Every review on Servitium is tied to a completed, paid job through the platform. No paid placements. No deleted complaints. No favours. What you see is what they've actually done.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ SECTION 4: HOW IT WORKS ══ */}
      <section id="how-it-works" className="py-20 md:py-28 bg-[#FAFAF9] scroll-mt-20" data-testid="customers-how-it-works">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#36454F] tracking-tight leading-tight text-center mb-14"
            data-testid="customers-how-headline"
          >
            How it works
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                num: '1',
                title: 'You connect with the business.',
                body: "Found them on Servitium, through a referral, on Instagram, or on a flyer — doesn't matter. They invite you into the job through the app.",
              },
              {
                num: '2',
                title: 'The job runs on rails.',
                body: 'Written quote. Scheduled date. In-app messages. Payment recorded through the platform. Everything on the record, nothing improvised.',
              },
              {
                num: '3',
                title: 'Reviewed and recorded.',
                body: "When the work's complete, you leave a review tied to that real job. The review becomes part of the business's permanent record — and your personal details stay private.",
              },
            ].map((card, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.06)] p-8 flex flex-col text-center"
                data-testid={`customers-how-card-${i}`}
              >
                <span className="text-6xl md:text-7xl font-extrabold text-[#880808] leading-none">{card.num}</span>
                <div className="w-10 h-[3px] bg-[#D4AF37] mx-auto mt-4 mb-6" />
                <h3 className="text-xl md:text-2xl font-extrabold text-[#1C1917] tracking-tight leading-tight mb-4">
                  {card.title}
                </h3>
                <p className="text-sm md:text-base text-[#57534E] leading-relaxed flex-grow">
                  {card.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ SECTION 5: WHAT YOU CAN ACTUALLY SEE ══ */}
      <section id="trust" className="py-20 md:py-28 bg-[#F8F6F0] scroll-mt-20" data-testid="customers-verified">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#1C1917] tracking-tight leading-tight mb-4"
              data-testid="customers-verified-headline"
            >
              "Verified" gets thrown around. Here's what it actually means on Servitium.
            </h2>
            <p className="text-base md:text-lg text-[#57534E] leading-relaxed max-w-2xl mx-auto">
              Most platforms hide behind vague badges. Servitium shows you the real signals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {[
              {
                Icon: ShieldCheck,
                title: 'Identity verified.',
                body: 'Every business owner on Servitium has confirmed their identity with government-issued ID. Mandatory. No exceptions.',
              },
              {
                Icon: Search,
                title: 'Optional background check by Certn.',
                body: 'Businesses can choose to add a third-party background check through Certn — the same service used by Canadian banks and property managers. When completed, it shows on their profile.',
              },
              {
                Icon: FileText,
                title: 'Credentials uploaded by the business.',
                body: "Businesses can upload their professional licenses, trade certifications, and other credentials directly to their profile. You can see the document itself before you engage — and decide what's enough for you.",
              },
              {
                Icon: ClipboardList,
                title: 'A real job record.',
                body: 'Every review on Servitium is tied to a completed, paid job through the platform. Not a Google review someone wrote three years ago. Not a curated testimonial. Real jobs, real outcomes, all on the record.',
              },
            ].map((card, i) => {
              const CardIcon = card.Icon;
              return (
                <div
                  key={i}
                  className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.06)] p-8 flex flex-col items-center text-center"
                  data-testid={`customers-badge-card-${i}`}
                >
                  {/* Topic icon */}
                  <div className="w-16 h-16 rounded-full border-[3px] border-[#D4AF37] bg-white flex items-center justify-center mb-6">
                    <CardIcon className="w-7 h-7 text-[#880808]" strokeWidth={2.25} />
                  </div>
                  <h3 className="text-xl md:text-2xl font-extrabold text-[#1C1917] tracking-tight leading-tight mb-3">
                    {card.title}
                  </h3>
                  <p className="text-sm md:text-base text-[#57534E] leading-relaxed">
                    {card.body}
                  </p>
                </div>
              );
            })}
          </div>

          <p className="text-lg md:text-xl text-[#1C1917] text-center mt-12" data-testid="customers-verified-closing">
            You see what's actually there. You decide what's enough.
          </p>
        </div>
      </section>

      {/* ══ SECTION 6: WHAT IT COSTS YOU ══ */}
      <section className="py-16 md:py-20 bg-[#FAFAF9]" data-testid="customers-cost">
        <div className="max-w-[640px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-xl md:text-2xl text-[#1C1917] tracking-tight mb-8" data-testid="customers-cost-headline">
            What does Servitium cost you?
          </h2>
          <p className="text-8xl md:text-9xl font-black text-[#880808] leading-none" data-testid="customers-cost-stat">
            $0.
          </p>
          <div className="w-20 md:w-24 h-[4px] bg-[#D4AF37] mx-auto mt-4 mb-6" />
          <p className="text-2xl md:text-3xl font-semibold text-[#1C1917] mb-8">
            Always. Forever.
          </p>
          <p className="text-lg text-[#57534E] leading-relaxed max-w-[600px] mx-auto">
            Customers never pay to use Servitium. Not for browsing, not for messaging, not for quotes, not for jobs. The platform is built on the businesses — not on you.
          </p>
        </div>
      </section>

      {/* ══ SECTION 7: WHY LOCAL. WHY SOLO. ══ */}
      <section className="py-12 md:py-20 bg-[#F8F6F0]" data-testid="customers-local">
        <div className="max-w-[720px] mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#1C1917] tracking-tight leading-tight mb-10"
            data-testid="customers-local-headline"
          >
            Built for local. Built for solo.
          </h2>

          <p className="text-lg md:text-xl text-[#36454F] leading-relaxed mb-8">
            Most service platforms exist to extract — taking a cut from every job, selling your data, ranking whoever paid the most for ads. Servitium exists for the solo operator: the one-person business, the family operation, the immigrant entrepreneur. The people who run the trades, the salons, the cleaning routes that actually hold neighborhoods together.
          </p>
          <p className="text-lg md:text-xl text-[#36454F] leading-relaxed">
            When you hire through Servitium, you're hiring real people in your own city — not a chain, not a bot, not a booking-platform farm dispatching unknown sub-contractors.
          </p>

          <div className="mt-12">
            <p className="text-xl md:text-2xl font-semibold text-[#1C1917] mb-3">The work stays local.</p>
            <p className="text-xl md:text-2xl font-semibold text-[#1C1917] mb-3">The money stays local.</p>
            <p className="text-xl md:text-2xl font-semibold text-[#1C1917]">The trust stays local.</p>
          </div>
        </div>
      </section>

      {/* ══ SECTION 8: FAQ ══ */}
      <section id="faq" className="py-20 md:py-28 bg-[#FAFAF9] scroll-mt-20" data-testid="customers-faq">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#1C1917] tracking-tight leading-tight text-center mb-12">
            Frequently asked questions
          </h2>

          <div className="space-y-0" data-testid="customers-faq-list">
            {[
              {
                q: 'What if something goes wrong with the job?',
                a: "Servitium isn\u2019t a referee \u2014 but every job creates a record. Written quote, in-app messages, scheduled dates, payment timeline. If something goes wrong, both sides have a documented history of what was actually agreed. That record is yours \u2014 much stronger than the text-and-cash trail you\u2019d have otherwise.",
              },
              {
                q: 'Is my information private?',
                a: "Yes. Your name, address, and contact details are only shared with the business for the specific job you\u2019ve engaged. Reviews are public \u2014 your personal information isn\u2019t.",
              },
              {
                q: 'Can businesses message me out of the blue?',
                a: 'No. Businesses cannot initiate contact with customers on Servitium. You decide when to reach out, and they respond.',
              },
              {
                q: 'Do I have to pay through the app?',
                a: "Yes \u2014 payment runs through Servitium so it\u2019s recorded, tied to the job, and connected to the review you leave afterward. No more cash with no receipt.",
              },
              {
                q: "What if I\u2019m not satisfied with the work?",
                a: "Your review becomes part of the business\u2019s permanent record on Servitium \u2014 that\u2019s the accountability built into the platform. If you have a specific concern about a job, you can also contact Servitium support directly.",
              },
              {
                q: 'Is Servitium only available in the Lower Mainland?',
                a: "We\u2019re rolling out neighborhood by neighborhood, starting in Metro Vancouver. Businesses across the Lower Mainland are coming online now.",
              },
            ].map((item, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} className="border-b border-[#E7E5E4]" data-testid={`customers-faq-${i}`}>
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full flex items-center justify-between py-5 text-left group"
                    data-testid={`customers-faq-toggle-${i}`}
                  >
                    <span className="text-base md:text-lg font-semibold text-[#36454F] group-hover:text-[#880808] transition-colors pr-4">
                      {item.q}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-[#880808] flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                      strokeWidth={2}
                    />
                  </button>
                  <div
                    className="overflow-hidden transition-all duration-300 ease-out"
                    style={{ maxHeight: isOpen ? 300 : 0, opacity: isOpen ? 1 : 0 }}
                  >
                    <p className="text-sm md:text-base text-[#57534E] leading-relaxed pb-5">
                      {item.a}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══ SECTION 9: FINAL CTA ══ */}
      <section id="final-cta" className="py-20 md:py-28 bg-[#FAFAF9] scroll-mt-20" data-testid="customers-final-cta">
        <div className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#1C1917] tracking-tight leading-tight mb-12"
            data-testid="customers-final-headline"
          >
            Hire local the way it should have always worked.
          </h2>

          <div className="flex items-center justify-center mb-6">
            <Link to="/customers/get" data-testid="customers-final-cta-download">
              <Button className="bg-[#880808] hover:bg-[#6b0606] text-white rounded-full px-10 py-5 text-lg font-bold shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105">
                Download
              </Button>
            </Link>
          </div>

          <p className="text-sm md:text-base italic text-[#57534E]" data-testid="customers-final-microcopy">
            Free for customers. Always.
          </p>
        </div>
      </section>

    </main>
  );
};

export default CustomersPage;